import { useState } from 'react';
import { Alert, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faChevronLeft, faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Card, CardHead, GhostBtn, WeekCalendar } from '../../components';
import { useBookingAssigneesQuery, useBookingsQuery } from '../../queries/scheduling';
import { errorText } from '../../utils/errors';
import BookingModal from './BookingModal';
import NewBookingModal from './NewBookingModal';
import { useCalendarModel } from './useCalendarModel';
import { CardNote, HeadTools, IconBtn, TodayBtn } from './styled';

const EMPTY = [];

export default function LeasingCalendar({ week, tz, weeklyHours, canManage }) {
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(null);
  const bookingsQuery = useBookingsQuery(week.range);
  const { data: assignees = EMPTY } = useBookingAssigneesQuery();
  const model = useCalendarModel({
    bookings: bookingsQuery.data || EMPTY,
    assignees,
    days: week.days,
    weeklyHours,
    tz
  });
  const selected = selectedId && bookingsQuery.data?.find((b) => b._id === selectedId);

  return (
    <Card aria-labelledby="calendar-title" aria-busy={bookingsQuery.isFetching}>
      <CardHead>
        <h2 id="calendar-title">Leasing Calendar</h2>
        <CardNote aria-live="polite">week of {dayjs(week.weekStart).format('MMM D')}</CardNote>
        <HeadTools>
          {canManage && (
            <>
              <GhostBtn type="button" onClick={() => setCreating('tour')}>
                <FontAwesomeIcon icon={faPlus} />
                New tour
              </GhostBtn>
              <GhostBtn type="button" onClick={() => setCreating('block')}>
                <FontAwesomeIcon icon={faBan} />
                Block time
              </GhostBtn>
            </>
          )}
          <IconBtn type="button" aria-label="Previous week" onClick={week.prev}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </IconBtn>
          <TodayBtn type="button" onClick={week.reset} disabled={week.isCurrent}>
            This week
          </TodayBtn>
          <IconBtn type="button" aria-label="Next week" onClick={week.next}>
            <FontAwesomeIcon icon={faChevronRight} />
          </IconBtn>
        </HeadTools>
      </CardHead>

      {bookingsQuery.error && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(bookingsQuery.error, 'Could not load bookings')} />
      )}

      {bookingsQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} title={false} />
      ) : (
        <WeekCalendar
          days={model.days}
          rows={model.rows}
          events={model.events}
          today={week.today}
          rowHeader="Agent"
          emptyText="No tours or appointments this week."
          stale={bookingsQuery.isPlaceholderData}
          onEventClick={setSelectedId}
        />
      )}

      {creating && <NewBookingModal type={creating} assignees={assignees} tz={tz} onClose={() => setCreating(null)} />}
      {selected && (
        <BookingModal
          booking={selected}
          assignees={assignees}
          tz={tz}
          canManage={canManage}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Card>
  );
}
